import client from "libs/client";
import { useEffect, useRef, useState } from "react";
import { useToast } from "ui-kit";
import { getVideoUploadErrorMessage } from "./videoUploadErrors";

/**
 * The video server has no bulk delete endpoint, so a selection is deleted one call at a time.
 * Calls are spaced out so each per-video notification stays on screen long enough to read.
 */
const DELETE_INTERVAL_MS = 600;

export interface BulkVideoDeleteProgress {
	total: number;
	deleted: number;
	failed: number;
}

function wait(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function getDeleteErrorMessage(error: unknown, videoId: number) {
	return getVideoUploadErrorMessage(error, `Video ${videoId} could not be deleted`);
}

export function useBulkVideoDelete() {
	const { addSuccessToast, addErrorToast } = useToast();
	const [progress, setProgress] = useState<BulkVideoDeleteProgress>();
	const isMountedRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Deletes already in flight are the admin's explicit intent, so they run to completion even if
	// the table unmounts; only the progress state is dropped once there is nothing left to render.
	const updateProgress = (next: BulkVideoDeleteProgress | undefined) => {
		if (isMountedRef.current) {
			setProgress(next);
		}
	};

	const deleteVideos = async (videoIds: number[]) => {
		let deleted = 0;
		let failed = 0;
		updateProgress({ total: videoIds.length, deleted, failed });

		for (const [index, videoId] of videoIds.entries()) {
			try {
				await client.deleteVideoUpload.mutation({ id: videoId });
				deleted += 1;
				addSuccessToast(`Video ${videoId} has been successfully deleted.`);
			} catch (error) {
				failed += 1;
				addErrorToast(getDeleteErrorMessage(error, videoId));
			}

			updateProgress({ total: videoIds.length, deleted, failed });

			if (index < videoIds.length - 1) {
				await wait(DELETE_INTERVAL_MS);
			}
		}

		client.queryClient.invalidateQueries({
			predicate: (query) => query.queryKey.includes("metadataVideos"),
		});

		updateProgress(undefined);
	};

	return { deleteVideos, progress };
}
